import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const LAUNCH_DATE = "March 1, 2026";
const LAUNCH_HOUR = 12; // noon ET
const LAUNCH_MINUTE = 0;
const EXPIRY_DATE = "March 3, 2026";
const SESSION_KEY = "yrd-tickets-banner-dismissed";

function isAfterNoonET(): boolean {
  // Get current time components in ET
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(new Date()).map(({ type, value }) => [type, value])
  );
  const nowMinutes = parseInt(parts.hour) * 60 + parseInt(parts.minute);
  const launchMinutes = LAUNCH_HOUR * 60 + LAUNCH_MINUTE;

  const nowDate = `${parts.year}-${parts.month}-${parts.day}`;
  const launchDate = new Date(LAUNCH_DATE);
  const launchDateStr = `${launchDate.getFullYear()}-${String(launchDate.getMonth() + 1).padStart(2, "0")}-${String(launchDate.getDate()).padStart(2, "0")}`;

  if (nowDate > launchDateStr) return true;
  if (nowDate < launchDateStr) return false;
  return nowMinutes >= launchMinutes;
}

function isPastExpiry(): boolean {
  const nowET = new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/New_York" })
  );
  const expiryET = new Date(
    new Date(EXPIRY_DATE).toLocaleString("en-US", {
      timeZone: "America/New_York",
    })
  );
  return nowET >= expiryET;
}

const TicketsLiveBanner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Don't render on /test-checkout
  const isExcluded = location.pathname === "/test-checkout";

  const checkTime = useCallback(() => {
    if (isPastExpiry()) return false;
    if (isAfterNoonET()) {
      setVisible(true);
      return true; // signal to clear interval
    }
    return false;
  }, []);

  useEffect(() => {
    if (dismissed || isExcluded) return;

    // Immediate check
    if (checkTime()) return;
    if (isPastExpiry()) return;

    const id = setInterval(() => {
      if (checkTime() || isPastExpiry()) {
        clearInterval(id);
      }
    }, 5000);

    return () => clearInterval(id);
  }, [dismissed, isExcluded, checkTime]);

  const handleDismiss = () => {
    setDismissed(true);
    setVisible(false);
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {}
  };

  const handleGetTickets = () => {
    if (location.pathname === "/") {
      // scroll to tickets section or navigate to /tickets
      navigate("/tickets");
    } else {
      navigate("/tickets");
    }
  };

  if (isExcluded || dismissed) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -80 }}
          animate={{ y: 0 }}
          exit={{ y: -80 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed top-0 left-0 right-0 z-[99990] bg-yacht-blue text-white"
        >
          <div className="relative px-4 py-3 md:py-4 flex flex-col items-center justify-center gap-2 md:gap-3">
            {/* Dismiss */}
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-3 md:top-3 md:right-4 text-white/80 hover:text-white transition-colors p-1"
              aria-label="Dismiss banner"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Text */}
            <p className="text-[15px] md:text-lg font-semibold text-center pr-6 md:pr-0">
              🎶 Tickets are NOW LIVE! Tap below to get yours.
            </p>

            {/* CTA */}
            <button
              onClick={handleGetTickets}
              className="bg-white text-yacht-blue font-bold text-sm md:text-base px-8 py-2.5 rounded-full w-full md:w-auto hover:bg-white/90 transition-colors"
            >
              GET TICKETS →
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TicketsLiveBanner;
